import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {Helper} from '../../helpers/helper.service';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {map, catchError} from 'rxjs/operators';
import { AttachPartsToProductBody } from '../Models/Part';

@Injectable()
export class ProductService {
  constructor(private helper: Helper, private http: HttpClient) {
  }

  public reArangeproduct(products) {
    for (const item in products.data) {
      if (products.data.hasOwnProperty(item)) {
        products.data[item].name = products.data[item].productItem.name;
        products.data[item].status = products.data[item].productItem.status;
      }
    }
    return products;
  }

  public getModals(gridData = new HttpParams()) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/model/model_items/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getProduct(gridData, params, scope = 'backoffice') {
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/product/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getInputType() {
    return this.http.get<any>(environment.apiUrl + '/backoffice/package/input-types')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getProductItem(productId, scope = 'backoffice') {
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/product/' + productId)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  // US-720: ViewProductComponent never reads product.translations (only
  // ManageProductComponent's multi-language edit-form tabs do) -- this is the same
  // product payload minus that one collection. BackOffice only; the view page doesn't
  // have a dealer-system variant.
  public getProductItemForView(productId) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/product/' + productId + '/view')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageProduct(product, isEdit, id = null) {
    if (isEdit) {
      return this.http.put(environment.apiUrl + '/backoffice/product/' + id, product)
        .pipe(catchError(res => this.helper.catchError(res)));
    }
    return this.http.post(environment.apiUrl + '/backoffice/product/', product)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  removeImage(fileId, productId) {
    return this.http.delete(environment.apiUrl + '/backoffice/product/' + productId + '/remove-image/' + fileId)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public managePackage(product, id = null) {
    return this.http.put(environment.apiUrl + '/backoffice/product/' + id + '/package', product)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public setOptions(products, optionId, scope = 'backoffice') {
    return this.http.put<any>(environment.apiUrl + '/' + scope + '/product/option/' + optionId,
      products)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public softDeleteProduct(id) {
    return this.http.delete(environment.apiUrl + '/backoffice/product/' + id)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public setStock(id, stock) {
    return this.http.put(environment.apiUrl + '/backoffice/product/' + id + '/stock', stock)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageProperties(id, properties) {
    return this.http.put(environment.apiUrl + '/backoffice/product/' + id + '/properties', properties)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  /** Carpets & Rugs: replaces the product's whole preset-size list — FR-006 */
  public manageSizes(id, sizes) {
    return this.http.put<any>(environment.apiUrl + '/backoffice/product/' + id + '/sizes', {productSizes: sizes})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageCollies(id, properties) {
    return this.http.put(environment.apiUrl + '/backoffice/product/' + id + '/collies', properties)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageStyleProperties(id, properties) {
    return this.http.put(environment.apiUrl + '/backoffice/product/' + id + '/style-properties', properties)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public readdProduct(id) {
    return this.http.put(environment.apiUrl + '/backoffice/product/' + id + '/reAdd', {id: id})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public setPosition(position, id = null) {
    return this.http.put(environment.apiUrl + '/backoffice/product/' + id + '/change-position', position)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  getPricesOfProduct(productId, scope = 'backoffice') {
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/product/' + productId + '/prices')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getStockProduct(gridData, productId, scope = 'backoffice') {
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/product/stocks/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  exportProducts(gridData = new HttpParams()) {
    return this.http.get(environment.apiUrl + '/backoffice/product/export?' + gridData.toString(), {
      responseType: 'arraybuffer' as 'json',
      observe: 'response'
    }).pipe(
      map((resp: HttpResponse<ArrayBuffer>) => {
        const contentDispositionHeader = resp.headers.get('content-disposition');
        const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        return {
          'url': window.URL.createObjectURL(new Blob([resp.body], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})),
          'filename': result.replace(/"/g, '')
        };
      }),
      catchError(res => this.helper.catchError(res))
    );
  }

  public getProductStyleProperties(productId, scope = 'backoffice') {
    return this.http.get<any>(environment.apiUrl + '/' + scope + '/product/' + productId + '/style-properties')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public setProductPackageExcludedOptionProducts(optionProducts, id) {
    return this.http.put(environment.apiUrl + '/backoffice/package/' + id + '/option/exclude', {optionProducts: optionProducts})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public attachPartsToProduct(body: AttachPartsToProductBody, id = null) {
    return this.http.put(environment.apiUrl + '/backoffice/part/product/' + id + '/attach-parts' , body)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public detachPartsFromProduct(partId: number, id = null) {
    return this.http.delete(environment.apiUrl + '/backoffice/part/' + partId + '/detach-product/' + id)
      .pipe(catchError(res => this.helper.catchError(res)));
  }
}
